import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Admin.css';

// โหลดรูปหลักฐานเป็น blob (endpoint ต้องใช้ token admin) แล้วแสดง thumbnail คลิกเพื่อดูขนาดเต็ม
function EvidenceThumb({ reqId }) {
    const [url, setUrl] = useState('');
    const [err, setErr] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = '';
        (async () => {
            try {
                const res = await api.get(`/admin/position-requests/${reqId}/evidence`, { responseType: 'blob' });
                objectUrl = URL.createObjectURL(res.data);
                if (!cancelled) setUrl(objectUrl);
                else URL.revokeObjectURL(objectUrl);
            } catch (e) {
                if (!cancelled) setErr(true);
            }
        })();
        return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [reqId]);

    if (err) return <span style={{ color: '#dc2626', fontSize: 13 }}>โหลดรูปไม่ได้</span>;
    if (!url) return <span style={{ fontSize: 13, color: 'var(--steam-text-muted)' }}>กำลังโหลดรูป…</span>;

    return (
        <a href={url} target="_blank" rel="noopener noreferrer" title="คลิกเพื่อดูขนาดเต็ม" style={{ display: 'inline-block' }}>
            <img
                src={url}
                alt="หลักฐานชื่อตำแหน่ง"
                style={{ maxWidth: 200, maxHeight: 140, borderRadius: 8, border: '1px solid var(--steam-border, #d1d5db)', objectFit: 'cover' }}
            />
        </a>
    );
}

function PositionRequests() {
    const [requests, setRequests] = useState([]);
    const [filter, setFilter] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });

    const fetchRequests = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/position-requests', { params: { status: filter } });
            setRequests(res.data || []);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'โหลดข้อมูลไม่สำเร็จ' });
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const formatDate = (d) =>
        d ? new Date(d).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

    const handleApprove = async (req) => {
        if (!window.confirm(`ยืนยันว่าตำแหน่งของ "${req.fullname}" คือ "${req.requested_position}" จริง?`)) return;
        setProcessing(req.id);
        setMessage({ type: '', text: '' });
        try {
            const res = await api.post(`/admin/position-requests/${req.id}/approve`);
            setMessage({ type: 'success', text: res.data.message || 'อนุมัติแล้ว' });
            fetchRequests();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'อนุมัติไม่สำเร็จ' });
        } finally {
            setProcessing(null);
        }
    };

    const handleReject = async (req) => {
        const reason = window.prompt(`เหตุผลที่ปฏิเสธคำขอแก้ไขตำแหน่งของ "${req.fullname}" (จะแจ้งทางอีเมล):`, '');
        if (reason === null) return;
        setProcessing(req.id);
        setMessage({ type: '', text: '' });
        try {
            const res = await api.post(`/admin/position-requests/${req.id}/reject`, { admin_note: reason });
            setMessage({ type: 'success', text: res.data.message || 'ปฏิเสธคำขอแล้ว' });
            fetchRequests();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ดำเนินการไม่สำเร็จ' });
        } finally {
            setProcessing(null);
        }
    };

    // ส่งอีเมลแจ้งผู้ใช้ให้กลับมาอัปโหลดหลักฐาน (เคสตกค้างที่ยังไม่มีหลักฐาน)
    const handleRequestEvidence = async (req) => {
        if (!window.confirm(`ส่งอีเมลแจ้ง "${req.fullname}" ให้กลับมาอัปโหลดหลักฐานประกอบคำขอ?`)) return;
        setProcessing(req.id);
        setMessage({ type: '', text: '' });
        try {
            const res = await api.post(`/admin/position-requests/${req.id}/request-evidence`);
            setMessage({ type: 'success', text: res.data.message || 'ส่งอีเมลแล้ว' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ส่งอีเมลไม่สำเร็จ' });
        } finally {
            setProcessing(null);
        }
    };

    const statusBadge = (status) => {
        const map = {
            pending: { cls: 'warning', text: 'รอดำเนินการ' },
            approved: { cls: 'success', text: 'อนุมัติแล้ว' },
            rejected: { cls: 'danger', text: 'ปฏิเสธ' }
        };
        const b = map[status] || { cls: 'secondary', text: status };
        return <span className={`badge badge-${b.cls}`}>{b.text}</span>;
    };

    const filters = [
        { key: 'pending', label: 'รอดำเนินการ' },
        { key: 'approved', label: 'อนุมัติแล้ว' },
        { key: 'rejected', label: 'ปฏิเสธ' },
        { key: 'all', label: 'ทั้งหมด' }
    ];

    return (
        <div className="admin-page">
            <div className="breadcrumb">
                <Link to="/admin">จัดการระบบ</Link>
                <span>/</span>
                <span>คำขอแก้ไขชื่อตำแหน่ง</span>
            </div>

            <div className="admin-header">
                <h1>คำขอแก้ไขชื่อตำแหน่ง</h1>
                <p>ตรวจสอบและยืนยันชื่อตำแหน่งที่ผู้ใช้ขอแก้ไข</p>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>{message.text}</div>
            )}

            <div className="filter-tabs" style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {filters.map(f => (
                    <button
                        key={f.key}
                        className={`steam-btn ${filter === f.key ? 'steam-btn-primary' : 'steam-btn-secondary'}`}
                        onClick={() => setFilter(f.key)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex-center" style={{ padding: '60px 0' }}>
                    <div className="spinner"></div>
                </div>
            ) : requests.length === 0 ? (
                <div className="steam-card">
                    <div className="steam-card-body" style={{ textAlign: 'center', padding: '48px 0', color: 'var(--steam-text-muted)' }}>
                        ไม่มีคำขอในสถานะนี้
                    </div>
                </div>
            ) : (
                <div className="position-requests-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {requests.map(req => (
                        <div key={req.id} className="steam-card">
                            <div className="steam-card-body">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, minWidth: 240 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                                            <h3 style={{ margin: 0, fontSize: 17 }}>{req.fullname || '-'}</h3>
                                            {statusBadge(req.status)}
                                        </div>
                                        <div className="ar-detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '6px 20px', fontSize: 14 }}>
                                            <div><span style={{ color: 'var(--steam-text-muted)' }}>ตำแหน่งเดิม:</span> {req.current_position || '-'}</div>
                                            <div style={{ gridColumn: '1 / -1' }}>
                                                <span style={{ color: 'var(--steam-text-muted)' }}>ตำแหน่งที่ขอแก้ไข:</span>{' '}
                                                <strong style={{ color: '#1e40af' }}>{req.requested_position}</strong>
                                            </div>
                                            <div>
                                                <span style={{ color: 'var(--steam-text-muted)' }}>อีเมลแจ้งผล:</span>{' '}
                                                {req.email ? (
                                                    req.email_verified
                                                        ? <>{req.email} <span style={{ color: '#16a34a' }}>(ยืนยันแล้ว)</span></>
                                                        : <>{req.email} <span style={{ color: '#dc2626' }}>(ยังไม่ยืนยัน)</span></>
                                                ) : <span style={{ color: '#dc2626' }}>ไม่มี</span>}
                                            </div>
                                            <div>
                                                <span style={{ color: 'var(--steam-text-muted)' }}>เบอร์ติดต่อกลับ:</span>{' '}
                                                {req.phone
                                                    ? <a href={`tel:${req.phone}`} style={{ color: '#2563eb' }}>{req.phone}</a>
                                                    : <span style={{ color: 'var(--steam-text-muted)' }}>-</span>}
                                            </div>
                                            <div><span style={{ color: 'var(--steam-text-muted)' }}>ยื่นเมื่อ:</span> {formatDate(req.created_at)}</div>
                                            {req.has_evidence ? (
                                                <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                                                    <span style={{ color: 'var(--steam-text-muted)', display: 'block', marginBottom: 4 }}>หลักฐาน (บัตรเจ้าหน้าที่/เอกสารราชการ):</span>
                                                    <EvidenceThumb reqId={req.id} />
                                                </div>
                                            ) : req.status === 'pending' ? (
                                                <div style={{ gridColumn: '1 / -1', marginTop: 4, color: '#b91c1c', fontSize: 13 }}>
                                                    ⚠️ ยังไม่มีหลักฐานแนบ — กด “ขอหลักฐานทางอีเมล” เพื่อให้ผู้ใช้กลับมาอัปโหลดหลักฐานก่อนพิจารณา
                                                </div>
                                            ) : null}
                                            {req.status !== 'pending' && (
                                                <div style={{ gridColumn: '1 / -1', color: 'var(--steam-text-muted)' }}>
                                                    ดำเนินการโดย: {req.processed_by_name || '-'} • {formatDate(req.processed_at)}
                                                    {req.admin_note ? ` • หมายเหตุ: ${req.admin_note}` : ''}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {req.status === 'pending' && (
                                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                            <button
                                                className="steam-btn steam-btn-primary"
                                                onClick={() => handleApprove(req)}
                                                disabled={processing === req.id}
                                            >
                                                {processing === req.id ? '...' : 'ยืนยันตำแหน่ง'}
                                            </button>
                                            <button
                                                className="steam-btn steam-btn-danger"
                                                onClick={() => handleReject(req)}
                                                disabled={processing === req.id}
                                            >
                                                ปฏิเสธ
                                            </button>
                                            {!req.has_evidence && (
                                                <button
                                                    className="steam-btn steam-btn-secondary"
                                                    onClick={() => handleRequestEvidence(req)}
                                                    disabled={processing === req.id}
                                                    title="ส่งอีเมลแจ้งผู้ใช้ให้กลับมาอัปโหลดหลักฐาน"
                                                >
                                                    📧 ขอหลักฐานทางอีเมล
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default PositionRequests;
